import React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import AppleStoreButtonEn from "../assets/en/apple-store-button-en.png";
import AppleStoreButtonFooterEn from "../assets/en/apple-store-blackbg-en.png";
import GooglePlayStoreButtonEn from "../assets/en/google-play-store-button-en.png";
import GooglePlayStoreButtonFooterEn from "../assets/en/google-play-blackbg-en.png";
import AppleStoreButtonHe from "../assets/he/apple-store-button-he.png";
import AppleStoreButtonFooterHe from "../assets/he/apple-store-blackbg-he.png";
import GooglePlayStoreButtonHe from "../assets/he/google-play-store-button-he.png";
import GooglePlayStoreButtonFooterHe from "../assets/he/google-play-blackbg-he.png";
import { device } from "../breakpoints";
import { APP_STORE_URL, GOOGLE_PLAY_URL } from "../constants/launch";
import { trackStoreButtonClick } from "../utils/pixels";

const badgeAssets = {
  hero: {
    en: {
      appStore: AppleStoreButtonEn,
      googlePlay: GooglePlayStoreButtonEn,
    },
    he: {
      appStore: AppleStoreButtonHe,
      googlePlay: GooglePlayStoreButtonHe,
    },
  },
  footer: {
    en: {
      appStore: AppleStoreButtonFooterEn,
      googlePlay: GooglePlayStoreButtonFooterEn,
    },
    he: {
      appStore: AppleStoreButtonFooterHe,
      googlePlay: GooglePlayStoreButtonFooterHe,
    },
  },
};

const StoreButtons = ({
  variant = "hero",
  store = "all",
  align = "center",
  mobileAlign,
  trackingPlacement,
}) => {
  const { i18n } = useTranslation();
  const currentLanguage =
    i18n.resolvedLanguage === "he" || i18n.language === "he" ? "he" : "en";
  const badgeVariant = variant === "footer" ? "footer" : "hero";
  const badges = badgeAssets[badgeVariant][currentLanguage];

  const stores = [
    {
      key: "appStore",
      href: APP_STORE_URL,
      image: badges.appStore,
      label:
        currentLanguage === "he"
          ? "להורדה ב-App Store"
          : "Download on the App Store",
    },
    {
      key: "googlePlay",
      href: GOOGLE_PLAY_URL,
      image: badges.googlePlay,
      label:
        currentLanguage === "he"
          ? "להורדה ב-Google Play"
          : "Get it on Google Play",
    },
  ].filter(({ key }) => store === "all" || store === key);

  const handleStoreClick = (storeKey, href) => {
    trackStoreButtonClick({
      store: storeKey,
      placement: trackingPlacement || variant,
      url: href,
    });
  };

  return (
    <ButtonsWrapper
      $variant={variant}
      $align={align}
      $mobileAlign={mobileAlign}
      dir={currentLanguage === "he" ? "rtl" : "ltr"}
    >
      {stores.map(({ key, href, image, label }) => (
        <StoreLink
          key={key}
          as={href ? "a" : "span"}
          href={href || undefined}
          target={href ? "_blank" : undefined}
          rel={href ? "noopener noreferrer" : undefined}
          aria-label={label}
          $isLinked={Boolean(href)}
          onClick={() => handleStoreClick(key, href)}
        >
          <BadgeImage src={image} alt={label} $variant={variant} />
        </StoreLink>
      ))}
    </ButtonsWrapper>
  );
};

export default StoreButtons;

const ButtonsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: ${({ $align }) =>
    $align === "start"
      ? "flex-start"
      : $align === "end"
      ? "flex-end"
      : "center"};
  align-items: center;
  gap: 12px;
  width: 100%;
  max-width: 560px;
  margin: ${({ $variant, $align }) =>
    $variant === "footer"
      ? "22px auto 0"
      : $align === "center"
      ? "0 auto"
      : "0"};

  @media ${device.pc} {
    gap: 16px;
  }

  @media (max-width: 520px) {
    justify-content: ${({ $mobileAlign, $align }) =>
      ($mobileAlign || $align) === "start"
        ? "flex-start"
        : ($mobileAlign || $align) === "end"
        ? "flex-end"
        : "center"};
  }
`;

const StoreLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  border-radius: 8px;
  line-height: 0;
  cursor: ${({ $isLinked }) => ($isLinked ? "pointer" : "default")};
  transition: transform 0.2s ease, filter 0.2s ease;

  &:hover {
    transform: ${({ $isLinked }) => ($isLinked ? "translateY(-2px)" : "none")};
    filter: ${({ $isLinked }) =>
      $isLinked ? "drop-shadow(0 8px 18px rgba(0, 0, 0, 0.22))" : "none"};
  }
`;

const BadgeImage = styled.img`
  display: block;
  width: auto;
  height: ${({ $variant }) => ($variant === "footer" ? "50px" : "48px")};
  object-fit: contain;

  @media (max-width: 360px) {
    height: 44px;
  }

  @media ${device.pc} {
    height: ${({ $variant }) => ($variant === "footer" ? "64px" : "58px")};
  }
`;
